import React, { useState } from 'react';
import { buildMonthSchedules } from '../../../api/adminApi';

const WEEKDAYS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
];

// `Offline` is a class taught in person — it links no content, which is why the
// calendar does not flag an empty one. The Ideal School batches run offline
// from June onward.
const MODES = ['Recorded', 'Live', 'Offline', 'Unit Test', 'Exam'];

/** The five buckets a plan can put a class in, and what each one means. */
const BUCKETS = [
  { key: 'added', label: 'Added', hint: 'New class, not previously planned.' },
  { key: 'moved', label: 'Moved', hint: 'Same class, new date — updated in place, keeping its contents.' },
  { key: 'updated', label: 'Updated', hint: 'Same class and date; another field changed.' },
  { key: 'cancelled', label: 'Cancelled', hint: 'Stored but absent from this plan.' },
  { key: 'unchanged', label: 'Unchanged', hint: 'Already correct — left untouched.' },
];

const currentMonth = () => new Date().toISOString().slice(0, 7);

/** "Tue 1 Sep" — the weekday matters, since the plan is authored by weekday. */
const formatSessionDate = (isoDate) => {
  const date = new Date(`${isoDate}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
  });
};

/**
 * Plans a month of classes in one pass: contents are dealt across the chosen
 * weekdays, then diffed against what is stored.
 *
 * The preview is always shown before anything is written. A move is applied as
 * an in-place date update rather than a delete plus a create, so a rescheduled
 * class keeps its contents, submissions and remarks.
 */
const MonthPlanner = ({ group, batchIds = [], chapterId, contentIds, faculty, onModeChange }) => {
  const [month, setMonth] = useState(currentMonth);
  const [weekdays, setWeekdays] = useState([1, 3, 5]);
  const [perSession, setPerSession] = useState(4);
  const [subject, setSubject] = useState('');
  const [time, setTime] = useState('18:30');
  const [mode, setMode] = useState('Recorded');
  const [facultyId, setFacultyId] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [startClassNumber, setStartClassNumber] = useState(1);

  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState(null);

  // The caller resolves which batches the plan covers — it owns both the batch
  // exclusions and the live group, and the plan must be written for exactly
  // that set.
  const canPlan = batchIds.length > 0
    && chapterId
    && (contentIds || []).length > 0
    && subject.trim() !== ''
    && weekdays.length > 0;

  const payload = () => ({
    batchIds,
    month,
    weekdays,
    chapterId,
    subject: subject.trim(),
    contentIds,
    perSession: Number(perSession),
    startClassNumber: Number(startClassNumber),
    time: time.trim() || null,
    mode,
    facultyId: facultyId ? Number(facultyId) : null,
    durationMinutes: Number(durationMinutes) || null,
  });

  const toggleWeekday = (value) => {
    setWeekdays((current) => (
      current.includes(value)
        ? current.filter((day) => day !== value)
        : [...current, value].sort((left, right) => left - right)
    ));
    setPreview(null);
  };

  const runPreview = async () => {
    setBusy(true);
    setError(null);
    setApplied(null);
    try {
      const result = await buildMonthSchedules({ ...payload(), dryRun: true });
      setPreview(result);
    } catch (previewError) {
      setError(previewError?.response?.data?.message || 'Failed to build the plan.');
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await buildMonthSchedules({ ...payload(), dryRun: false });
      setApplied(result);
      setPreview(result);
    } catch (applyError) {
      setError(applyError?.response?.data?.message || 'Failed to save the plan.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sb-panel">
      <div className="sb-panel-head">
        <h4>3. Plan the month</h4>
        <span className="sb-selcount">
          {group ? `${batchIds.length} batches` : 'No group selected'}
          {(contentIds || []).length > 0 ? ` · ${contentIds.length} contents` : ''}
        </span>
      </div>

      <div className="sb-form">
        <label>
          Month
          <input type="month" value={month} onChange={(e) => { setMonth(e.target.value); setPreview(null); }} />
        </label>
        <label>
          Subject
          <input
            type="text"
            value={subject}
            placeholder="Physics"
            onChange={(e) => { setSubject(e.target.value); setPreview(null); }}
          />
        </label>
        <div className="sb-field">
          <label htmlFor="month-planner-mode">Mode</label>
          <select
            id="month-planner-mode"
            value={mode}
            onChange={(e) => {
              setMode(e.target.value);
              setPreview(null);
              // The chapter picker sits above this control and uses the mode to
              // float the bucket a class of that kind is scheduled from.
              if (onModeChange) onModeChange(e.target.value);
            }}
          >
            {MODES.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
        <label>
          Time
          <input type="text" value={time} onChange={(e) => { setTime(e.target.value); setPreview(null); }} />
        </label>
        <label>
          Duration (min)
          <input
            type="number"
            min="1"
            value={durationMinutes}
            onChange={(e) => { setDurationMinutes(e.target.value); setPreview(null); }}
          />
        </label>
        <label>
          Contents / session
          <input
            type="number"
            min="1"
            value={perSession}
            onChange={(e) => { setPerSession(e.target.value); setPreview(null); }}
          />
        </label>
        <label>
          First class number
          <input
            type="number"
            min="1"
            value={startClassNumber}
            onChange={(e) => { setStartClassNumber(e.target.value); setPreview(null); }}
          />
        </label>
        <div className="sb-field">
          <label htmlFor="month-planner-faculty">Faculty</label>
          <select
            id="month-planner-faculty"
            value={facultyId}
            onChange={(e) => { setFacultyId(e.target.value); setPreview(null); }}
          >
            <option value="">— unassigned —</option>
            {(faculty || []).map((person) => (
              <option key={person.id} value={person.id}>{person.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="sb-pattern">
        <span className="sb-pattern-label">Session days</span>
        {WEEKDAYS.map((day) => (
          <button
            key={day.value}
            type="button"
            className={`sb-daytoggle${weekdays.includes(day.value) ? ' on' : ''}`}
            aria-pressed={weekdays.includes(day.value)}
            onClick={() => toggleWeekday(day.value)}
          >
            {day.label}
          </button>
        ))}
      </div>

      {error && <div className="cal-error">{error}</div>}

      <div className="sb-form-foot">
        <span className="sb-selcount">
          {canPlan ? 'Preview shows what saving would do — nothing is written yet.' : 'Pick a group, a chapter and its contents first.'}
        </span>
        <div className="sb-actions">
          <button type="button" className="sb-btn" disabled={!canPlan || busy} onClick={runPreview}>
            {busy && !applied ? 'Working…' : 'Preview'}
          </button>
          <button
            type="button"
            className="sb-btn sb-btn-primary"
            disabled={!preview || busy || applied !== null}
            onClick={apply}
          >
            Apply plan
          </button>
        </div>
      </div>

      {preview && (
        <div className="sb-preview">
          <div className="sb-preview-head">
            <strong>{preview.sessions}</strong> sessions across{' '}
            <strong>{preview.sessionDates}</strong> session days ·{' '}
            <strong>{preview.contentsPlaced}</strong> contents placed
            {preview.contentsUnplaced > 0 && (
              <span className="sb-preview-warn">
                {' '}· {preview.contentsUnplaced} did not fit this month
              </span>
            )}
          </div>

          {/* The dated list: counts alone never said *when* class 12 runs or what
              it covers, which is the whole point of previewing before writing. */}
          {(preview.plannedSessions || []).length > 0 && (
            <div className="sb-sessionplan">
              <h5>Sessions this plan writes</h5>
              <ol className="sb-sessionplan-list">
                {preview.plannedSessions.map((session) => (
                  <li key={`${session.date}-${session.classNumber}`} className="sb-sessionplan-row">
                    <span className="sb-sessionplan-date">{formatSessionDate(session.date)}</span>
                    <span className="sb-sessionplan-class">Class {session.classNumber}</span>
                    <span
                      className="sb-sessionplan-contents"
                      title={session.contents.map((content) => content.title).join('\n')}
                    >
                      {session.contents.length === 0
                        ? 'No contents'
                        : session.contents
                          .map((content) => content.number || content.title)
                          .join(', ')}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {preview.contentsUnplaced > 0 && (
            <div className="sb-warnbox">
              The month ran out of session days before these contents were placed:{' '}
              {preview.unplacedTitles.slice(0, 5).join(', ')}
              {preview.unplacedTitles.length > 5 ? `, +${preview.unplacedTitles.length - 5} more` : ''}.
              Add weekdays, raise contents per session, or plan them into the next month.
            </div>
          )}

          <div className="sb-bucketgrid">
            {BUCKETS.map((bucket) => (
              <div key={bucket.key} className={`sb-bucket sb-bucket-${bucket.key}`}>
                <span className="sb-bucket-count">{preview.summary[bucket.key]}</span>
                <span className="sb-bucket-label">{bucket.label}</span>
                <span className="sb-bucket-hint">{bucket.hint}</span>
              </div>
            ))}
          </div>

          {preview.diff.moved.length > 0 && (
            <div className="sb-movelist">
              <h5>Moves — applied in place, keeping contents and history</h5>
              {preview.diff.moved.slice(0, 10).map((move) => (
                <div key={move.scheduleId} className="sb-moverow">
                  <span className="sb-badge">#{move.scheduleId}</span>
                  <span className="sb-move-from">{move.from}</span>
                  <span aria-hidden="true">→</span>
                  <span className="sb-move-to">{move.to}</span>
                  <span className="sb-selcount">batch {move.session.batchId}</span>
                </div>
              ))}
              {preview.diff.moved.length > 10 && (
                <span className="sb-selcount">+{preview.diff.moved.length - 10} more</span>
              )}
            </div>
          )}

          {applied && (
            <div className="sb-result">
              Saved — {applied.created} created, {applied.updated} updated, {applied.moved} moved.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MonthPlanner;
